import {
  of as observableOf,
  throwError as observableThrowError,
  Observable,
  combineLatest,
} from 'rxjs';

import {mergeMap, map, catchError, startWith, shareReplay, switchMap, take} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {
  HttpHeaders,
  HttpClient,
  HttpResponse,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import {Resource, ResourceWithPermissionHints} from './resource';
import {
  FilterResult,
  FilterResultWithState,
  SearchLimitation,
  SkippedFilterResult,
} from './filterresult';
import {CacheService} from '../cache/cache.service';
import {User} from '../cache/currentuser/user';
import {AttributeTypeDescription} from '../cache/schema/attributetypedescription';
import {environment} from '../../environments/environment';
import {LayoutService} from '../layout/layout.service';
import {Endpoint} from '../layout/endpoint';
import {EnvironmentConfigService} from '../core/services/environment-config.service';
import {APPCONFIG} from '../config';
import {LanguageService} from '../language/language.service';
import {getApiErrorCode} from '../error/api-error';

export enum ResourceUpdateStatus {
  ApprovalPending,
  Success,
  Fail,
}

export interface SearchTerm {
  /** original term text including quotes - used for tokenization */
  raw: string;
  /** term text without the surrounding/inner quotes */
  value: string;
  /** fully quoted term - matched with equality instead of the tiered search */
  exact: boolean;
}

/** all XPath filters the tiered search can use for one set of terms and attributes */
export interface TierFilters {
  allExact: boolean;
  noSearchPossible: boolean;
  singleRequestOnly: boolean;
  /** a non-exact term with whitespace needs the third (reordered / partial words) phase */
  needsInexactTier: boolean;
  startsWithFilter: string | null;
  containsFilter: string | null;
  containsFilterExclusive: string | null;
  inexactFilter: string | null;
  inexactFilterExclusive: string | null;
}

export type SearchPlanMode = 'tiered' | 'single' | 'none' | 'noSearchPossible';
export type SearchSingleFilter = 'inexact' | 'contains' | 'startsWith';

interface SearchRung {
  mode: 'tiered' | 'single';
  /** tiered: highest phase to run (1..3) */
  level?: number;
  /** single: which filter the one request uses */
  single?: SearchSingleFilter;
}

/** how a search is executed so that every request stays within the query string budget */
export interface SearchPlan {
  mode: SearchPlanMode;
  terms: SearchTerm[];
  attributes: AttributeTypeDescription[];
  filters: TierFilters | null;
  /** tiered: highest phase that is run (1..3) */
  level: number;
  /** single: the filter of the one request */
  singleFilter: string | null;
  /** tiered: filter of the total-count request when a page is already full (null = not needed) */
  countFilter: string | null;
  /** whether the search had to be reduced */
  limited: boolean;
  limitation: SearchLimitation;
}

interface PlannedSearchRequest {
  terms: SearchTerm[];
  searchAttributes: AttributeTypeDescription[];
  xpath: string;
  xpathParts: RegExpExecArray;
  sorted: boolean;
  measure: (filter: string) => number;
  attributes: string;
  pagesize: number;
  startIndex: number;
  sortAttribute: string;
  sortAsc: boolean;
}

@Injectable()
export class ResourceService {
  public static guidRegex = new RegExp('^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$');
  public static timestampRegex: RegExp =
    /^[12]\d\d\d-(0\d|1[012])-([012]\d|3[01])T([01]\d|2[0123]):[0-5]\d:[0-5]\d.\d\d\d$/;

  // Search filters travel as GET query parameters. Two server-side limits apply:
  //  - IIS request filtering and ASP.NET reject query strings longer than their configured limit
  //    (default 2048). The service web.config raises both to 16384:
  //      <security><requestFiltering><requestLimits maxQueryString="16384" /></requestFiltering></security>
  //      <httpRuntime maxQueryStringLength="16384" maxUrlLength="20480" />
  //  - http.sys rejects a request whose request line AND headers together exceed MaxRequestBytes
  //    (default 16384). With Windows authentication the Kerberos Authorization header alone is
  //    several KB, so the usable query budget is lower and differs per user.
  // Searches therefore start with APPCONFIG.maxSearchQueryLength as budget, degrade the search
  // (phases, attributes, terms) until every request fits, and lower the budget for the session
  // whenever the server still rejects a request because of its length (see runPlannedSearch).
  // absolute limit: never send a query string longer than the web.config maxQueryString
  public static readonly ABSOLUTE_MAX_QUERY_STRING_LENGTH = 16384;
  // hard upper bound for the number of ';'-separated search terms
  public static readonly MAX_SEARCH_TERMS = 50;
  // a rejected request shorter than this is never interpreted as "query too long"
  private static readonly MIN_QUERY_LENGTH_FOR_TOO_LONG_DETECTION = 1500;
  private static readonly MAX_BUDGET_RETRIES = 5;
  private static readonly BUDGET_STORAGE_KEY = 'identitydirector.searchQueryBudget';
  // %LoginID% / %ObjectID% are replaced with a 36 character GUID before a request is sent
  private static readonly MEASURE_GUID = '00000000-0000-0000-0000-000000000000';
  // filters are measured with worst-case pageSize/index digits so every page gets the same plan
  private static readonly MEASURE_INDEX = 9999999;
  private static effectiveSearchQueryBudget: number = null;

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private layoutService: LayoutService,
    private envConfig: EnvironmentConfigService,
  ) {}

  private static configuredMaxSearchQueryLength(): number {
    const configured = Number(APPCONFIG.maxSearchQueryLength);
    if (!(configured > 0)) {
      return ResourceService.ABSOLUTE_MAX_QUERY_STRING_LENGTH;
    }
    return Math.min(Math.floor(configured), ResourceService.ABSOLUTE_MAX_QUERY_STRING_LENGTH);
  }

  /**
   * Query string budget for searches: the configured maximum, lowered for this browser session
   * after the server rejected a request because of its length.
   */
  public static getEffectiveSearchQueryBudget(): number {
    const max = ResourceService.configuredMaxSearchQueryLength();
    if (ResourceService.effectiveSearchQueryBudget == null) {
      let stored: number = null;
      try {
        const value = sessionStorage.getItem(ResourceService.BUDGET_STORAGE_KEY);
        if (value != null) stored = parseInt(value, 10);
      } catch (e) {
        stored = null;
      }
      ResourceService.effectiveSearchQueryBudget =
        stored != null && !isNaN(stored) && stored > 0 && stored < max ? stored : max;
    }
    return Math.min(ResourceService.effectiveSearchQueryBudget, max);
  }

  public static setEffectiveSearchQueryBudget(budget: number): void {
    ResourceService.effectiveSearchQueryBudget = Math.max(1, Math.floor(budget));
    try {
      sessionStorage.setItem(
        ResourceService.BUDGET_STORAGE_KEY,
        String(ResourceService.effectiveSearchQueryBudget),
      );
    } catch (e) {
      // session storage not available - the budget is only kept in memory
    }
  }

  public static resetEffectiveSearchQueryBudget(): void {
    ResourceService.effectiveSearchQueryBudget = null;
    try {
      sessionStorage.removeItem(ResourceService.BUDGET_STORAGE_KEY);
    } catch (e) {
      // session storage not available
    }
  }

  /**
   * Builds the query parameters of a v2/resources search exactly as searchResources sends them,
   * so the length of a request can be measured before it is sent.
   */
  public static buildSearchParams(
    filter: string,
    attributes: string,
    pagesize: number,
    startIndex: number,
    sortAttribute: string,
    sortAsc: boolean,
    sortParamName: 'sortingAttribute' | 'sortAttribute',
  ): HttpParams {
    let params = new HttpParams()
      .append('filter', filter)
      .append('attributes', attributes)
      .append('pageSize', '' + pagesize)
      .append('index', '' + startIndex);

    if (sortAttribute != null) {
      params = params.append(sortParamName, sortAttribute);
      if (!sortAsc) {
        params = params.append('sortDirection', 'desc');
      }
    }
    return params;
  }

  public searchResources(
    filter: string,
    attributes: string,
    pagesize: number,
    startIndex: number = 0,
    sortAttribute: string = null,
    sortAsc: boolean = true,
  ): Observable<FilterResult> {
    if (filter == null || filter.length === 0) {
      return observableThrowError("Invalid Filter-XPath: '" + filter + "'");
    }

    const attributesRegex = new RegExp(
      '^([a-zA-Z_:][a-zA-Z0-9-._:]*)(,([a-zA-Z_:][a-zA-Z0-9-._:]*)+)*$',
    );
    if (!attributesRegex.test(attributes)) {
      return observableThrowError('Invalid attributes: ' + attributes);
    }

    if (pagesize % 1 !== 0 || pagesize < 1) {
      return observableThrowError('Invalid PageSize: ' + pagesize);
    }

    if (startIndex % 1 !== 0 || startIndex < 0) {
      return observableThrowError('Invalid StartIndex: ' + startIndex);
    }

    if (sortAttribute != null) {
      const attributeRegex = new RegExp('^[a-zA-Z_:][a-zA-Z0-9-._:]*$');
      if (!attributeRegex.test(sortAttribute)) {
        return observableThrowError('Invalid Sort Attribute: ' + sortAttribute);
      }
    }

    let resolvedFilter: Observable<string>;
    if (filter.includes('%LoginID%') || filter.includes('%ObjectID%')) {
      //Replace %LoginID% with ObjectID of Current User
      resolvedFilter = this.cacheService.getCurrentUser().pipe(
        take(1),
        map((user: User) => filter.replace(/%LoginID%/g, user.id).replace(/%ObjectID%/g, user.id)),
      );
    } else {
      resolvedFilter = observableOf(filter);
    }

    return combineLatest([resolvedFilter, this.layoutService.endpoint$.pipe(take(1))]).pipe(
      switchMap(([finalFilter, env]: [string, Endpoint]) => {
        const params = ResourceService.buildSearchParams(
          finalFilter,
          attributes,
          pagesize,
          startIndex,
          sortAttribute,
          sortAsc,
          env.isCloud() ? 'sortAttribute' : 'sortingAttribute',
        );

        // never send a request the server is guaranteed to reject because of its length
        const queryLength = params.toString().length;
        if (queryLength > ResourceService.ABSOLUTE_MAX_QUERY_STRING_LENGTH) {
          return observableThrowError(() => ResourceService.createQueryTooLongError(queryLength));
        }

        return this.http
          .get<FilterResult>(env.getUrl() + 'v2/resources/', {
            headers: this.generateHeaders(),
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(
            catchError((error: HttpErrorResponse) =>
              ResourceService.handleError(ResourceService.tagQueryLength(error, queryLength)),
            ),
          );
      }),
    );
  }

  public searchResourcesNextPage(nextPageUrl: string): Observable<FilterResult> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        return this.http
          .get<FilterResult>(nextPageUrl, {
            headers: this.generateHeaders(),
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(catchError(ResourceService.handleError));
      }),
    );
  }

  public getResource(id: string): Observable<Resource> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid id: ' + id);
    }

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const url: string = env.getUrl() + 'v2/resources/' + id + '/';
        return this.http
          .get<Resource>(url, {
            headers: this.generateHeaders(),
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(catchError(ResourceService.handleGetError));
      }),
    );
  }

  public getBasicResource(id: string): Observable<Resource> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid ObjectID: ' + id);
    }
    const attrs = this.envConfig.getAttributes();
    const types = this.envConfig.getObjectTypes();
    let params = new HttpParams()
      .append('filter', '/' + types.Resource + '[' + attrs.ObjectID + "='" + id + "']")
      .append('attributes', [attrs.ObjectType, attrs.ObjectID, attrs.DisplayName].join(','))
      .append('pageSize', '1');

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        return this.http
          .get<FilterResult>(env.getUrl() + 'v2/resources/', {
            headers: this.generateHeaders(),
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(
            map(response => {
              if (response == null || response.Results.length === 0) return null;
              return response.Results[0];
            }),
            catchError(ResourceService.handleError),
          );
      }),
    );
  }

  public createResourceWithData(data: any): Observable<string | ResourceUpdateStatus> {
    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    if (data[objectIdAttr] != null) {
      delete data[objectIdAttr];
    }

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.generateHeaders(),
          observe: 'response',
        };
        if (!env.isCloud()) {
          options.withCredentials = true;
        }

        return this.http
          .post<any>(env.getUrl() + 'v2/resources/', JSON.stringify(data), options)
          .pipe(
            map((response: HttpResponse<any>) => {
              if (response.status === 202) {
                // see https://github.com/lithnet/resourcemanagement-webservice/wiki/Error-handling#v2-api
                return ResourceUpdateStatus.ApprovalPending;
              } else {
                if (env.isCloud()) {
                  console.log(response);
                  return response.body[this.envConfig.getAttributeName('ObjectID')];
                } else {
                  const location: string = response.headers.get('Location');
                  const locationRegex: RegExp =
                    /.*\/([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})\/?$/;
                  const matches = locationRegex.exec(location);
                  if (matches == null)
                    throw Error('Create not sending valid location of new resource.');
                  return matches[1];
                }
              }
            }),
            catchError(ResourceService.handleError),
          );
      }),
    );
  }

  public updateOrCreateResourceAndGetId(
    id: string,
    changes: any,
    create: boolean,
  ): Observable<string | ResourceUpdateStatus> {
    if (create === true) {
      return this.createResourceWithData(changes);
    } else {
      return this.updateResource(id, changes).pipe(
        map(result => (result === ResourceUpdateStatus.Success ? id : result)),
      );
    }
  }

  public updateOrCreateResource(
    id: string,
    changes: any,
    create: boolean,
  ): Observable<ResourceUpdateStatus> {
    return this.updateOrCreateResourceAndGetId(id, changes, create).pipe(
      map(result => {
        if (typeof result === 'string') {
          if (!(result === null || result === undefined) && result.length !== 0) {
            return ResourceUpdateStatus.Success;
          } else {
            return ResourceUpdateStatus.Fail;
          }
        } else {
          return result;
        }
      }),
    );
  }

  public updateResource(
    id: string,
    changes: any,
    locale: string = null,
  ): Observable<ResourceUpdateStatus> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid id: ' + id);
    }

    let params: HttpParams = new HttpParams();

    if (locale != null && locale !== '*' && locale !== 'NeutralLocale') {
      params = params.append('locale', locale);
    }
    let jsonChanges: string = JSON.stringify(changes);
    if (jsonChanges === '{}') {
      return observableOf<ResourceUpdateStatus>(ResourceUpdateStatus.Success);
    }
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.generateHeaders(),
          observe: 'response',
          params: params,
        };
        if (!env.isCloud()) {
          options.withCredentials = true;
        }
        let url: string = env.getUrl() + 'v2/resources/' + id + '/';
        return this.http.patch<any>(url, jsonChanges, options).pipe(
          map((response: HttpResponse<any>) => {
            if (response.status === 202) {
              // see https://github.com/lithnet/resourcemanagement-webservice/wiki/Error-handling#v2-api
              return ResourceUpdateStatus.ApprovalPending;
            } else if (response.ok) {
              return ResourceUpdateStatus.Success;
            } else {
              return ResourceUpdateStatus.Fail;
            }
          }),
          catchError(ResourceService.handleError),
        );
      }),
    );
  }

  public deleteResource(id: string): Observable<boolean> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid id: ' + id);
    }

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options: any = {
          headers: this.generateHeaders(),
          observe: 'response',
        };
        if (!env.isCloud()) {
          options.withCredentials = true;
        }
        let url: string = env.getUrl() + 'v2/resources/' + id + '/';
        return this.http.delete<any>(url, options).pipe(
          map((response: HttpResponse<any>) => {
            return response.ok;
          }),
          catchError(ResourceService.handleError),
        );
      }),
    );
  }

  public getResourceWithPermissionHints(id: string): Observable<ResourceWithPermissionHints> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid ObjectID in getResourceWithPermissionHints: ' + id);
    }
    let params = new HttpParams()
      .append('includePermissionHints', 'true')
      .append('includeNullAttributes', 'true');
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options = {
          headers: this.generateHeaders(),
          params: params,
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        const url: string = env.getUrl() + 'v2/resources/' + id + '/';
        return this.http
          .get<ResourceWithPermissionHints>(url, options)
          .pipe(catchError(ResourceService.handleGetError));
      }),
    );
  }

  public getResourceWithPermissionHintsAndLanguage(
    id: string,
    langConf: string,
  ): Observable<ResourceWithPermissionHints> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid ObjectID in : ' + id);
    }

    if (langConf == null) {
      return observableOf(null);
    }
    let params = new HttpParams()
      .append('includePermissionHints', 'true')
      .append('includeNullAttributes', 'true');
    if (langConf != '*' && langConf != 'NeutralLocale') {
      params = params.append('locale', langConf);
    }
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        const options = {
          headers: this.generateHeaders(),
          params: params,
          ...(!env.isCloud() ? {withCredentials: true} : {}),
        };
        let url: string = env.getUrl() + 'v2/resources/' + id + '/';
        return this.http
          .get<ResourceWithPermissionHints>(url, options)
          .pipe(catchError(ResourceService.handleGetError));
      }),
    );
  }

  public static splitSearchTerm(searchTerm: string): string[] {
    const tokens: string[] = [];

    let quoteActive: boolean = false;
    let currentToken: string = '';

    for (let c of searchTerm) {
      if (quoteActive) {
        if (c === '"') {
          quoteActive = false;
          if (currentToken !== '') tokens.push(currentToken);
          currentToken = '';
        } else {
          currentToken += c;
        }
      } else {
        if (c.match(/^\s+$/)) {
          // whitespace
          if (currentToken !== '') tokens.push(currentToken);
          currentToken = '';
        } else if (c === '"') {
          if (currentToken !== '') tokens.push(currentToken);
          currentToken = '';
          quoteActive = true;
        } else {
          currentToken += c;
        }
      }
    }
    if (currentToken !== '') tokens.push(currentToken);

    return tokens;
  }

  public static splitTermsOnSemicolon(searchTerm: string): string[] {
    const terms: string[] = [];

    let quoteActive: boolean = false;
    let currentTerm: string = '';

    for (let c of searchTerm) {
      if (c === '"') {
        quoteActive = !quoteActive;
        currentTerm += c;
      } else if (c === ';' && !quoteActive) {
        if (currentTerm.trim() !== '') terms.push(currentTerm.trim());
        currentTerm = '';
      } else {
        currentTerm += c;
      }
    }
    if (currentTerm.trim() !== '') terms.push(currentTerm.trim());

    return terms;
  }

  public static parseSearchTerms(searchTerm: string): SearchTerm[] {
    const terms: SearchTerm[] = [];

    ResourceService.splitTermsOnSemicolon(searchTerm).forEach(raw => {
      const exactMatch = /^"([^"]*)"$/.exec(raw);
      const value = exactMatch != null ? exactMatch[1] : raw.replace(/"/g, '');
      if (value.trim() !== '') {
        terms.push({raw: raw, value: value, exact: exactMatch != null});
      }
    });

    return terms;
  }

  private static spliceFilter(
    xpath: string,
    xpathParts: RegExpExecArray,
    ...predicates: string[]
  ): string {
    if (xpathParts != null) {
      return xpathParts[1] + '[(' + xpathParts[2] + ') and (' + predicates.join(') and (') + ')]';
    }
    if (predicates.length === 1) {
      return xpath + '[' + predicates[0] + ']';
    }
    return xpath + '[(' + predicates.join(') and (') + ')]';
  }

  /**
   * Length of the query string searchResources would send for this filter. Measured with the
   * OnPrem sort parameter name (the longer one), worst-case page size / index digits and the
   * placeholders expanded, so the result is the same for every page of a search.
   */
  public static measureQueryLength(
    filter: string,
    attributes: string,
    pagesize: number,
    sortAttribute: string,
    sortAsc: boolean,
  ): number {
    if (filter == null) return 0;
    const resolved = filter.replace(/%LoginID%|%ObjectID%/g, ResourceService.MEASURE_GUID);
    return ResourceService.buildSearchParams(
      resolved,
      attributes,
      pagesize * 2,
      ResourceService.MEASURE_INDEX,
      sortAttribute,
      sortAsc,
      'sortingAttribute',
    ).toString().length;
  }

  public static buildTierFilters(
    terms: SearchTerm[],
    validSearchAttributes: AttributeTypeDescription[],
    xpath: string,
    xpathParts: RegExpExecArray,
  ): TierFilters {
    const multiTerm = terms.length > 1;
    const allExact = terms.every(t => t.exact);
    const needsInexactTier = terms.some(t => !t.exact && /\s/.test(t.value));

    const exactNeedlesFor = (value: string): string[] => {
      const needles: string[] = [];
      validSearchAttributes.forEach((atd: AttributeTypeDescription) => {
        if (atd.datatype === 'String' || atd.datatype === 'Text') {
          needles.push(atd.systemname + '="' + value + '"');
        } else if (atd.datatype === 'Integer' && /^-?\d+$/.test(value)) {
          needles.push(atd.systemname + '=' + value);
        }
      });
      return needles;
    };

    const perTermStartsWith: string[] = terms.map(term => {
      if (term.exact) return exactNeedlesFor(term.value).join(' or ');
      const needles: string[] = [];
      validSearchAttributes.forEach((atd: AttributeTypeDescription) => {
        if (atd.datatype === 'String' || atd.datatype === 'Text') {
          needles.push('starts-with(' + atd.systemname + ',"' + term.value + '")');
        } else if (atd.datatype === 'Integer' && /^-?\d+$/.test(term.value)) {
          needles.push(atd.systemname + '=' + term.value);
        }
      });
      return needles.join(' or ');
    });

    const perTermContains: string[] = terms.map(term => {
      if (term.exact) return exactNeedlesFor(term.value).join(' or ');
      const needles: string[] = [];
      validSearchAttributes.forEach((atd: AttributeTypeDescription) => {
        if (atd.datatype === 'String' || atd.datatype === 'Text') {
          needles.push('starts-with(' + atd.systemname + ',"%' + term.value + '")');
        }
        // Integer not possible - cannot check for contains
      });
      return needles.join(' or ');
    });

    const perTermInexact: string[] = terms.map(term => {
      if (term.exact) return '(' + exactNeedlesFor(term.value).join(' or ') + ')';
      const termNeedles: string[] = [];
      ResourceService.splitSearchTerm(term.raw).forEach(searchVal => {
        const argNeedles: string[] = [];
        validSearchAttributes.forEach((atd: AttributeTypeDescription) => {
          if (atd.datatype === 'String' || atd.datatype === 'Text') {
            argNeedles.push('starts-with(' + atd.systemname + ',"%' + searchVal + '")');
          } else if (atd.datatype === 'Integer' && /^-?\d+$/.test(searchVal)) {
            argNeedles.push(atd.systemname + '=' + searchVal);
          }
        });
        if (argNeedles.length > 0) {
          termNeedles.push('(' + argNeedles.join(' or ') + ')');
        }
      });
      return termNeedles.join(' and ');
    });

    const joinTerms = (fragments: string[]): string => {
      const nonEmpty = fragments.filter(f => f !== '');
      if (nonEmpty.length === 0) return '';
      return multiTerm ? nonEmpty.map(e => '(' + e + ')').join(' or ') : nonEmpty[0];
    };

    const startsWithSearch = joinTerms(perTermStartsWith);
    const containsSearch = joinTerms(perTermContains);
    const inexactSearch = joinTerms(perTermInexact);

    const noSearchPossible = inexactSearch === '';
    const singleRequestOnly = startsWithSearch === '';

    const startsWithFilter =
      startsWithSearch === ''
        ? null
        : ResourceService.spliceFilter(xpath, xpathParts, startsWithSearch);
    const containsFilter =
      containsSearch === ''
        ? null
        : ResourceService.spliceFilter(xpath, xpathParts, containsSearch);
    const inexactFilter =
      inexactSearch === '' ? null : ResourceService.spliceFilter(xpath, xpathParts, inexactSearch);
    const containsFilterExclusive =
      containsSearch === '' || allExact || startsWithSearch === ''
        ? null
        : ResourceService.spliceFilter(
            xpath,
            xpathParts,
            containsSearch,
            'not(' + startsWithSearch + ')',
          );
    const inexactFilterExclusive =
      containsSearch === '' || inexactSearch === ''
        ? null
        : ResourceService.spliceFilter(
            xpath,
            xpathParts,
            inexactSearch,
            'not(' + containsSearch + ')',
          );

    return {
      allExact,
      noSearchPossible,
      singleRequestOnly,
      needsInexactTier,
      startsWithFilter,
      containsFilter,
      containsFilterExclusive,
      inexactFilter,
      inexactFilterExclusive,
    };
  }

  private static singleFilterFor(filters: TierFilters, key: SearchSingleFilter): string {
    switch (key) {
      case 'inexact':
        return filters.inexactFilter;
      case 'contains':
        return filters.containsFilter;
      default:
        return filters.startsWithFilter;
    }
  }

  // every request a rung sends; a null entry means the rung is not applicable for these filters
  private static requiredFilters(filters: TierFilters, rung: SearchRung): string[] {
    if (rung.mode === 'single') {
      return [ResourceService.singleFilterFor(filters, rung.single)];
    }
    switch (rung.level) {
      case 3:
        return [
          filters.startsWithFilter,
          filters.containsFilterExclusive,
          filters.inexactFilterExclusive,
          filters.inexactFilter,
        ];
      case 2:
        return [filters.startsWithFilter, filters.containsFilterExclusive, filters.containsFilter];
      default:
        return [filters.startsWithFilter];
    }
  }

  // rungs from the most complete search down to the cheapest one
  private static candidateRungs(filters: TierFilters, sorted: boolean): SearchRung[] {
    if (filters.singleRequestOnly) {
      return [{mode: 'single', single: 'inexact'}];
    }
    if (filters.allExact) {
      // equality only - when sorted the inexact filter holds the same needles
      return [{mode: 'single', single: sorted ? 'inexact' : 'startsWith'}];
    }
    if (sorted) {
      // a sorted result needs a single request; the inexact filter includes all other levels
      return [
        {mode: 'single', single: 'inexact'},
        {mode: 'single', single: 'contains'},
        {mode: 'single', single: 'startsWith'},
      ];
    }
    const rungs: SearchRung[] = [];
    if (filters.needsInexactTier) {
      rungs.push({mode: 'tiered', level: 3});
    }
    rungs.push({mode: 'tiered', level: 2}, {mode: 'tiered', level: 1});
    return rungs;
  }

  // largest k < attributes.length so that the rung fits with the first k attributes (0 = none)
  private static largestFittingAttributePrefix(
    terms: SearchTerm[],
    attributes: AttributeTypeDescription[],
    xpath: string,
    xpathParts: RegExpExecArray,
    rung: SearchRung,
    budget: number,
    measure: (filter: string) => number,
  ): number {
    const requiredFor = (k: number): string[] =>
      ResourceService.requiredFilters(
        ResourceService.buildTierFilters(terms, attributes.slice(0, k), xpath, xpathParts),
        rung,
      );
    // The filter length grows with every attribute; a prefix without any usable attribute has no
    // filter yet and counts as length 0 here, which is checked after the search.
    const lengthFor = (k: number): number =>
      Math.max(0, ...requiredFor(k).map(f => (f == null ? 0 : measure(f))));

    let low = 1;
    let high = attributes.length - 1;
    let best = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (lengthFor(mid) <= budget) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    if (best === 0) return 0;
    return requiredFor(best).some(f => f == null) ? 0 : best;
  }

  private static createPlan(
    mode: 'ok' | 'none' | 'noSearchPossible',
    allTerms: SearchTerm[],
    terms: SearchTerm[],
    allAttributes: AttributeTypeDescription[],
    attributes: AttributeTypeDescription[],
    filters: TierFilters,
    rung: SearchRung,
  ): SearchPlan {
    let planMode: SearchPlanMode = mode === 'ok' ? 'tiered' : mode;
    let level = 0;
    let singleFilter: string = null;
    let countFilter: string = null;
    if (mode === 'ok') {
      if (rung.mode === 'single') {
        planMode = 'single';
        singleFilter = ResourceService.singleFilterFor(filters, rung.single);
      } else {
        level = rung.level;
        // total count when the page is already full: must count exactly the objects the
        // executed levels can page through
        if (level >= 3) {
          countFilter = filters.inexactFilter;
        } else if (level === 2) {
          countFilter = filters.containsFilter;
        }
      }
    }

    const needsInexactTier = filters != null && filters.needsInexactTier;
    const skippedInexactTier =
      needsInexactTier &&
      ((planMode === 'tiered' && level < 3) ||
        (planMode === 'single' && rung.single !== 'inexact'));
    const skippedContainsTier =
      filters != null &&
      !filters.allExact &&
      ((planMode === 'tiered' && level < 2) ||
        (planMode === 'single' && rung.single === 'startsWith'));
    const droppedTermCount = allTerms.length - terms.length;

    const limitation: SearchLimitation = {
      totalTermCount: allTerms.length,
      usedTermCount: terms.length,
      droppedTermCount: droppedTermCount,
      totalAttributeCount: allAttributes.length,
      usedAttributeCount: attributes.length,
      skippedContainsTier: skippedContainsTier,
      skippedInexactTier: skippedInexactTier,
      budgetReduced: false,
    };
    const limited =
      planMode === 'none' ||
      droppedTermCount > 0 ||
      attributes.length < allAttributes.length ||
      skippedContainsTier ||
      skippedInexactTier;

    return {
      mode: planMode,
      terms,
      attributes,
      filters,
      level,
      singleFilter,
      countFilter,
      limited,
      limitation,
    };
  }

  /**
   * Chooses how a search is executed so that every request stays within the query budget.
   * Order of sacrifice: search phases (detailed, then contains), then trailing attributes of the
   * search scope, then trailing search terms. Never fails - when nothing fits the plan is 'none'.
   */
  public static planSearch(
    allTerms: SearchTerm[],
    allAttributes: AttributeTypeDescription[],
    xpath: string,
    xpathParts: RegExpExecArray,
    sorted: boolean,
    budget: number,
    measure: (filter: string) => number,
  ): SearchPlan {
    const fits = (filters: string[]): boolean =>
      filters.every(f => f != null && measure(f) <= budget);
    const cappedTerms = allTerms.slice(0, ResourceService.MAX_SEARCH_TERMS);

    for (let termCount = cappedTerms.length; termCount >= 1; termCount--) {
      const terms = cappedTerms.slice(0, termCount);
      const full = ResourceService.buildTierFilters(terms, allAttributes, xpath, xpathParts);
      if (full.noSearchPossible) {
        return ResourceService.createPlan(
          'noSearchPossible',
          allTerms,
          terms,
          allAttributes,
          allAttributes,
          full,
          null,
        );
      }
      const rungs = ResourceService.candidateRungs(full, sorted);

      // 1. phases first: the most complete rung whose requests all fit with every attribute
      for (const rung of rungs) {
        if (fits(ResourceService.requiredFilters(full, rung))) {
          return ResourceService.createPlan(
            'ok',
            allTerms,
            terms,
            allAttributes,
            allAttributes,
            full,
            rung,
          );
        }
      }

      // 2. then attributes: the cheapest rung with the longest attribute prefix that fits
      const cheapest = rungs[rungs.length - 1];
      const prefix = ResourceService.largestFittingAttributePrefix(
        terms,
        allAttributes,
        xpath,
        xpathParts,
        cheapest,
        budget,
        measure,
      );
      if (prefix > 0) {
        const attributes = allAttributes.slice(0, prefix);
        const filters = ResourceService.buildTierFilters(terms, attributes, xpath, xpathParts);
        return ResourceService.createPlan(
          'ok',
          allTerms,
          terms,
          allAttributes,
          attributes,
          filters,
          cheapest,
        );
      }

      // 3. then terms: drop the last term and try again
    }

    return ResourceService.createPlan('none', allTerms, [], allAttributes, [], null, null);
  }

  private static emptyFinishedResult(): FilterResultWithState {
    const result = new FilterResultWithState();
    result.TotalCount = 0;
    result.HasMoreItems = false;
    result.Results = [];
    result.Finished = true;
    return result;
  }

  private searchWithoutTerms(
    xpath: string,
    attributes: string,
    pagesize: number,
    startIndex: number,
    sortAttribute: string,
    sortAsc: boolean,
  ): Observable<FilterResultWithState> {
    return this.searchResources(
      xpath,
      attributes,
      pagesize,
      startIndex,
      sortAttribute,
      sortAsc,
    ).pipe(
      map(fr => {
        const frws = <FilterResultWithState>fr;
        frws.Finished = true;
        return frws;
      }),
    );
  }

  public searchAndFilterResources(
    xpath: string,
    searchTerm: string,
    searchAttributes: string[],
    attributes: string,
    pagesize: number,
    startIndex: number = 0,
    sortAttribute: string = null,
    sortAsc: boolean = true,
  ): Observable<FilterResultWithState> {
    xpath = xpath.trim();

    searchTerm = searchTerm ? searchTerm.trim() : null;
    if (
      searchTerm == null ||
      searchTerm.length === 0 ||
      searchAttributes == null ||
      searchAttributes.length === 0
    ) {
      return this.searchWithoutTerms(
        xpath,
        attributes,
        pagesize,
        startIndex,
        sortAttribute,
        sortAsc,
      );
    }

    // ';' separates independent search terms combined with OR; a fully quoted term ("...")
    // is matched exactly instead of with the tiered starts-with/contains search
    const parsedTerms: SearchTerm[] = ResourceService.parseSearchTerms(searchTerm);
    if (parsedTerms.length === 0) {
      // only quotes/semicolons/whitespace left - treat like an empty search box
      return this.searchWithoutTerms(
        xpath,
        attributes,
        pagesize,
        startIndex,
        sortAttribute,
        sortAsc,
      );
    }

    return this.cacheService.getAttributeTypeDescriptions().pipe(
      mergeMap((atds: AttributeTypeDescription[]) => {
        let validSearchAttributes: AttributeTypeDescription[] = [];
        searchAttributes.forEach((attr: string) => {
          const objectTypeAttr = this.envConfig.getAttributeName('ObjectType');
          if (attr === objectTypeAttr) return;
          atds.forEach((atd: AttributeTypeDescription) => {
            if (attr === atd.systemname && ['Reference', 'Binary'].indexOf(atd.datatype) === -1) {
              validSearchAttributes.push(atd);
            }
          });
        });
        if (validSearchAttributes.length === 0) {
          return observableOf(ResourceService.emptyFinishedResult());
        }
        const xpathParts = /^([^[]+)\[(.+)\]$/.exec(xpath);

        const request: PlannedSearchRequest = {
          terms: parsedTerms,
          searchAttributes: validSearchAttributes,
          xpath,
          xpathParts,
          sorted: sortAttribute != null,
          measure: (filter: string) =>
            ResourceService.measureQueryLength(
              filter,
              attributes,
              pagesize,
              sortAttribute,
              sortAsc,
            ),
          attributes,
          pagesize,
          startIndex,
          sortAttribute,
          sortAsc,
        };
        return this.runPlannedSearch(request, 0, false);
      }),
    );
  }

  // plans and executes the search; when the server still rejects a request because of its
  // length, the session budget is lowered below that length and the search is re-planned
  private runPlannedSearch(
    request: PlannedSearchRequest,
    attempt: number,
    budgetReduced: boolean,
  ): Observable<FilterResultWithState> {
    const budget = ResourceService.getEffectiveSearchQueryBudget();
    const plan = ResourceService.planSearch(
      request.terms,
      request.searchAttributes,
      request.xpath,
      request.xpathParts,
      request.sorted,
      budget,
      request.measure,
    );
    if (budgetReduced) {
      plan.limitation.budgetReduced = true;
      plan.limited = true;
    }

    return this.executePlan(plan, request).pipe(
      catchError((error: any) => {
        if (
          attempt < ResourceService.MAX_BUDGET_RETRIES &&
          ResourceService.isQueryTooLongError(error)
        ) {
          const failedLength: number =
            typeof error.queryLength === 'number' ? error.queryLength : budget;
          const newBudget = Math.min(budget, failedLength) - 1;
          if (newBudget >= 1) {
            console.warn(
              'Search request rejected because of its length (' +
                failedLength +
                ' characters). Lowering the search query budget to ' +
                newBudget +
                ' and retrying.',
            );
            ResourceService.setEffectiveSearchQueryBudget(newBudget);
            return this.runPlannedSearch(request, attempt + 1, true);
          }
        }
        return observableThrowError(() => error);
      }),
    );
  }

  private executePlan(
    plan: SearchPlan,
    request: PlannedSearchRequest,
  ): Observable<FilterResultWithState> {
    const attachLimitation = (fr: FilterResult): FilterResultWithState => {
      const frws = <FilterResultWithState>fr;
      if (frws != null && plan.limited) {
        frws.Limitation = plan.limitation;
      }
      return frws;
    };

    switch (plan.mode) {
      case 'noSearchPossible':
      case 'none':
        return observableOf(attachLimitation(ResourceService.emptyFinishedResult()));
      case 'single':
        // a single request contains all results
        return this.searchResources(
          plan.singleFilter,
          request.attributes,
          request.pagesize,
          request.startIndex,
          request.sortAttribute,
          request.sortAsc,
        ).pipe(
          map(fr => {
            const frws = attachLimitation(fr);
            frws.Finished = true;
            return frws;
          }),
        );
      default:
        return this.executeTieredPlan(plan, request).pipe(map(attachLimitation));
    }
  }

  private executeTieredPlan(
    plan: SearchPlan,
    request: PlannedSearchRequest,
  ): Observable<FilterResultWithState> {
    const attributes = request.attributes;
    const pagesize = request.pagesize;
    const startIndex = request.startIndex;
    const objectIdAttr = this.envConfig.getAttributeName('ObjectID');
    const filters = plan.filters;

    // first level search (starts with / exact matches)
    const firstLevel: Observable<FilterResult> = this.searchResources(
      filters.startsWithFilter,
      attributes,
      pagesize,
      startIndex,
    ).pipe(shareReplay(1));

    // second level search (contains) - only when the plan includes it
    let secondLevel: Observable<FilterResult>;
    if (plan.level < 2 || filters.containsFilterExclusive == null) {
      secondLevel = observableOf(new SkippedFilterResult());
    } else {
      secondLevel = firstLevel.pipe(
        mergeMap((startsWithFR: FilterResult) => {
          if (startsWithFR.Results.length >= pagesize) {
            // skip second-level search, already enough objects in first level
            return observableOf(new SkippedFilterResult());
          }
          /*249
            100 exit after first level
            100 exit after first level
            49 continue to second level

            200-249+49 = 0
            300-249+0 = 51*/
          return this.searchResources(
            filters.containsFilterExclusive,
            attributes,
            startIndex === 0 ? pagesize : pagesize + startsWithFR.Results.length,
            startIndex === 0
              ? 0
              : startIndex - startsWithFR.TotalCount + startsWithFR.Results.length,
          );
        }),
        shareReplay(1),
      );
    }

    // third level search (reordered / partial words) - only together with the second level
    let thirdLevel: Observable<FilterResult>;
    if (plan.level >= 3 && filters.inexactFilterExclusive != null) {
      thirdLevel = firstLevel.pipe(
        mergeMap((startsWithFR: FilterResult) => {
          if (startsWithFR.Results.length >= pagesize) {
            return observableOf(new SkippedFilterResult());
          }

          return secondLevel.pipe(
            mergeMap((containsFR: FilterResult) => {
              if (containsFR == null || containsFR instanceof SkippedFilterResult) {
                return observableOf(new SkippedFilterResult());
              }
              const firstLevelOIDs = startsWithFR.Results.map((res: Resource) => res[objectIdAttr]);
              const secondLevelOIDs = containsFR.Results.map((res: Resource) => res[objectIdAttr]);
              const OIDs = new Set([...firstLevelOIDs, ...secondLevelOIDs]);

              if (OIDs.size >= pagesize) {
                return observableOf(new SkippedFilterResult());
              }

              const totalCount = startsWithFR.TotalCount + containsFR.TotalCount;

              return this.searchResources(
                filters.inexactFilterExclusive,
                attributes,
                startIndex === 0 ? pagesize : pagesize + OIDs.size,
                startIndex === 0 ? 0 : startIndex - totalCount + OIDs.size,
              ).pipe(shareReplay(1));
            }),
          );
        }),
      );
    } else {
      thirdLevel = observableOf(new SkippedFilterResult());
    }

    // total count over every executed level when a page is already full; not needed for a
    // first-level-only plan because its TotalCount is exact
    const totalCountSearch: Observable<FilterResult> =
      plan.countFilter == null
        ? null
        : this.searchResources(plan.countFilter, objectIdAttr, 1).pipe(shareReplay(1));
    const finishWithTotalCount = (fr: FilterResultWithState): Observable<FilterResultWithState> => {
      fr.Finished = true;
      if (totalCountSearch == null) {
        return observableOf(fr);
      }
      return totalCountSearch.pipe(
        map((countFR: FilterResult) => {
          fr.TotalCount = countFR.TotalCount;
          return fr;
        }),
      );
    };

    // combining
    return combineLatest([
      firstLevel,
      secondLevel.pipe(startWith(null)),
      thirdLevel.pipe(startWith(null)),
    ]).pipe(
      mergeMap(results => {
        //first level
        const startsWithFR: FilterResult = results[0];
        const containsFR: FilterResult = results[1];
        const inexactFR: FilterResult = results[2];

        // get correct total size for all the results of all level searches if level 1 already exceeds pagesize
        if (startsWithFR.Results.length >= pagesize) {
          return finishWithTotalCount(<FilterResultWithState>startsWithFR);
        }

        if (containsFR !== null && containsFR instanceof SkippedFilterResult) {
          const fr = <FilterResultWithState>startsWithFR;
          fr.Finished = true;
          return observableOf(fr);
        } else if (containsFR !== null) {
          // get combined results of first level and second level search and third level search
          let finalFR = new FilterResultWithState();
          finalFR.TotalCount = startsWithFR.TotalCount + containsFR.TotalCount;

          // add first level objects
          finalFR.Results = startsWithFR.Results.slice();
          const finalObjectIDs = finalFR.Results.map((res: Resource) => res[objectIdAttr]);

          // add second level objects
          containsFR.Results.forEach((res: Resource) => {
            if (
              finalFR.Results.length < pagesize &&
              finalObjectIDs.indexOf(res[objectIdAttr]) === -1
            ) {
              finalFR.Results.push(res);
              finalObjectIDs.push(res[objectIdAttr]);
            }
          });

          // return values of first and second level if page size is exceeded
          if (finalFR.Results.length >= pagesize) {
            return finishWithTotalCount(finalFR);
          }

          if (inexactFR !== null && inexactFR instanceof SkippedFilterResult) {
            finalFR.Finished = true;
          } else if (inexactFR !== null) {
            // add third level objects
            finalFR.TotalCount += inexactFR.TotalCount;
            finalFR.Finished = true;

            inexactFR.Results.forEach((res: Resource) => {
              if (
                finalFR.Results.length < pagesize &&
                finalObjectIDs.indexOf(res[objectIdAttr]) === -1
              ) {
                finalFR.Results.push(res);
              }
            });
          }
          return observableOf(finalFR);
        } else {
          return observableOf(<FilterResultWithState>startsWithFR);
        }
      }),
    );
  }

  public requestApproval(
    id: string,
    decision: 'approve' | 'reject',
    reason: string,
  ): Observable<boolean> {
    if (!ResourceService.guidRegex.test(id)) {
      return observableThrowError('Invalid id: ' + id);
    }

    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        if (!env.supportsApprovals()) {
          return observableThrowError(() => ({
            code: 'not-supported',
            message: 'Approvals are not available on this backend.',
          }));
        }
        let url: string = env.getUrl() + 'v2/approvals/' + id + '/' + decision;

        let data: string = null;
        if (reason != null && reason.length > 0) {
          data = JSON.stringify({Reason: reason});
        }
        const options: any = {
          headers: this.generateHeaders(),
          observe: 'response',
        };
        if (!env.isCloud()) {
          options.withCredentials = true;
        }
        return this.http.post<any>(url, data, options).pipe(
          map((r: HttpResponse<any>) => r.ok),
          catchError(ResourceService.handleError),
        );
      }),
    );
  }

  private static createQueryTooLongError(queryLength: number): Error {
    const error: any = new Error(
      'The request query string is too long (' +
        queryLength +
        ' characters) and was not sent. Please reduce the search or filter.',
    );
    error.queryLength = queryLength;
    error.queryTooLong = true;
    return error;
  }

  /**
   * Whether an error is a rejection because of the request length. http.sys and IIS answer such
   * requests with 400 (Request Too Long), 404.14/404.15 or 414 and an HTML or empty body, whereas
   * the service answers every error with a JSON object containing an error code.
   */
  public static isQueryTooLongError(error: any): boolean {
    if (error == null || typeof error !== 'object') return false;
    if (error.queryTooLong === true) return true;
    const queryLength = error.queryLength;
    if (
      typeof queryLength !== 'number' ||
      queryLength < ResourceService.MIN_QUERY_LENGTH_FOR_TOO_LONG_DETECTION
    ) {
      return false;
    }
    if ([400, 404, 413, 414, 431].indexOf(error.status) === -1) return false;
    const body = error.error;
    if (body != null && typeof body === 'object' && ('error' in body || 'code' in body)) {
      return false;
    }
    return true;
  }

  // lets the search planner recognise requests that were rejected because of their length
  private static tagQueryLength(error: any, queryLength: number): any {
    if (error != null && typeof error === 'object') {
      error.queryLength = queryLength;
    }
    return error;
  }

  private static handleError(error: HttpErrorResponse) {
    //TODO: Check if this error output to console is really unnecessary (error should be checked later on)
    //console.error(error);
    try {
      return observableThrowError(error || {code: 'server-error'});
    } catch (e) {
      return observableThrowError('' + error.status + ' ' + error.statusText);
    }
  }

  private static handleGetError(error: HttpErrorResponse) {
    if (getApiErrorCode(error) === 'resource-not-found') {
      console.info(error);
      return observableOf(null);
    } else {
      console.error(error);
      return observableThrowError(error || 'Server error');
    }
  }

  /**
   * User-facing description of a limited search, e.g. for a snackbar.
   */
  public static searchLimitationMessage(
    limitation: SearchLimitation,
    languageResource: string,
  ): string {
    if (limitation == null) return null;
    if (limitation.usedTermCount === 0) {
      return LanguageService.translate(
        'DatatableSearchLimitedNone',
        'The search text is too long for this view and could not be executed.',
        languageResource,
      );
    }
    const details: string[] = [];
    if (limitation.skippedInexactTier || limitation.skippedContainsTier) {
      details.push(
        LanguageService.translate(
          'DatatableSearchLimitedPhases',
          'detailed matching (reordered or partial words) was skipped',
          languageResource,
        ),
      );
    }
    if (limitation.usedAttributeCount < limitation.totalAttributeCount) {
      details.push(
        LanguageService.translate(
          'DatatableSearchLimitedAttributes',
          'only the first {0} of {1} attributes were searched',
          languageResource,
          [String(limitation.usedAttributeCount), String(limitation.totalAttributeCount)],
        ),
      );
    }
    if (limitation.droppedTermCount > 0) {
      details.push(
        LanguageService.translate(
          'DatatableSearchLimitedTerms',
          'only the first {0} search terms were used',
          languageResource,
          [String(limitation.usedTermCount)],
        ),
      );
    }
    const lead = LanguageService.translate(
      'DatatableSearchLimited',
      'The search text is too long for this view and was limited',
      languageResource,
    );
    if (details.length === 0) {
      return lead + '.';
    }
    return lead + ': ' + details.join(', ') + '.';
  }

  public static convertCamelCaseToNormal(string: string) {
    if (string == null || string.trim().length === 0) return null;
    return string.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase());
  }

  private generateHeaders(): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders({'Content-Type': 'application/json'});
    return headers;
  }
}
